import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { Context } from "context/Wrapper";
import { collection, query, where, getDocs, orderBy, limit } from "firebase/firestore";
import { db } from "config/config";
import AdvancedTable from "components/AdvancedTable";
import tableConfig from "config/advancedTableColumnsControl.json";
import useCustomParams from "components/shared/useCustomParams";


const fetchDocsByIds = async (collectionName, ids) => {
  const unique = [...new Set((ids || []).filter(Boolean))];
  if (unique.length === 0) return {};

  const result = {};
  const batchSize = 10; // Firestore "in" supports up to 10

  for (let i = 0; i < unique.length; i += batchSize) {
    const batchIds = unique.slice(i, i + batchSize);
    const q = query(collection(db, collectionName), where("__name__", "in", batchIds));
    const snap = await getDocs(q);
    snap.forEach((d) => {
      result[d.id] = d.data();
    });
  }
  return result;
};


export const DashboardJobsTable = () => {
  const context = useContext(Context);
  const navigate = useNavigate();
  const { groupId, isPro, userId } = context?.state?.userProfile || {};
  const { showAllDataFlag } = context?.state || {};
  const { params } = useCustomParams();

  const tableId = "dashboard-jobs";
  const pageSize = Number(params.get(`${tableId}_pageSize`)) || 10;
  const page = Number(params.get(`${tableId}_page`)) || 0;
  const fetchLimit = (page + 1) * pageSize;



  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);


  useEffect(() => {
    const fetchRecentJobs = async () => {
      try {
        setLoading(true);

        const jobsRef = collection(db, "projects");

        const conditions = [];
        if (showAllDataFlag && groupId) {
          conditions.push(where("groupId", "==", groupId));
        } else if (!showAllDataFlag && userId) {
          conditions.push(where("userId", "==", userId));
        }

        conditions.push(orderBy("dateUpdated", "desc"));
        conditions.push(limit(fetchLimit));
        const q = query(jobsRef, ...conditions);
        const snapshot = await getDocs(q);

        // ✅ Extract only the 10 jobs
        const baseJobs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

        // ✅ Collect only the related IDs for THESE 10 jobs
        const contactIds = [];
        const buildingIds = [];
        const profileIds = [];

        for (const j of baseJobs) {
          if (j.client) contactIds.push(j.client);
          if (j.tenant) contactIds.push(j.tenant);
          if (j.applicant) contactIds.push(j.applicant);
          if (j.filingRep) contactIds.push(j.filingRep);
          if (j.projectManager) contactIds.push(j.projectManager);

          if (j.building) buildingIds.push(j.building);
          if (j.assignedTo) profileIds.push(j.assignedTo);
        }

        // ✅ Fetch ONLY the related docs (not entire collections)
        const [contacts, buildings, profiles] = await Promise.all([
          fetchDocsByIds("contacts", contactIds),
          fetchDocsByIds("buildings", buildingIds),
          fetchDocsByIds("profiles", profileIds),
        ]);

        // ✅ Enrich the 10 jobs
        const jobsData = baseJobs.map((data) => {
          const client = contacts[data.client] || {};
          const tenant = contacts[data.tenant] || {};
          const building = buildings[data.building] || {};
          const filingRep = contacts[data.filingRep] || {};
          const projectManager = contacts[data.projectManager] || {};
          const applicant = contacts[data.applicant] || {};
          const profile = profiles[data.assignedTo] || {};

          const buildingAddress = `${building?.houseNumber || ""} ${building?.streetName || ""}`.trim();

          return {
            ...data,
            clientString: client.businessName || "",
            tenantString: tenant.businessName || "",
            applicantString: applicant.fullName || "",
            buildingString: building.customerReference || "",
            buildingAddress,
            buildingAddress_lc: buildingAddress.toLowerCase(),
            filingRepName: filingRep.businessName || filingRep.fullName || "",
            projectManagerName: projectManager.businessName || projectManager.fullName || "",
            assignedTo: profile.name || "",
          };
        });

        setJobs(jobsData);
      } catch (error) {
        console.error("Error fetching jobs:", error);
      } finally {
        setLoading(false);
      }
    };


    if (userId) {
      fetchRecentJobs();
    }
  }, [userId, showAllDataFlag, groupId, isPro, page, pageSize]);

  const dateUpdatedColumn = {
    field: "dateUpdated",
    headerName: "Last Updated",
    renderCell: ({ row }) => {
      if (!row.dateUpdated) return "";
      const d = typeof row.dateUpdated === "number"
        ? new Date(row.dateUpdated)
        : row.dateUpdated.toDate();
      return d.toISOString().split("T")[0];
    },
  };

  const columns = [
    {
      field: "customerReference",
      headerName: "Job ID",
      flex: 1,
    },
    {
      field: "building",
      headerName: "Building",
      flex: 1,
    },
    {
      field: "buildingAddress",
      headerName: "Address",
      flex: 1,
    },
    {
      field: "floors",
      headerName: "Floor(s)",
    },
    {
      field: "clientString",
      headerName: "Client",
      flex: 1,
    },
    {
      field: "tenant_lc",
      headerName: "Tenant",
    },
    {
      field: "DOBNumber",
      headerName: "DOB Number",
    },
    {
      field: "applicant",
      headerName: "Applicant ID",
      renderCell: ({ row }) => row.applicant || "",

    },
    {
      field: "applicant_lc",
      headerName: "Applicant Name",

    },
    {
      field: "building",
      headerName: "Building ID",
    },
    {
      field: "client",
      headerName: "Client ID",
    },
    {
      field: "filingRep_lc",
      headerName: "Filing Rep",
    },
    {
      field: "assignedTo_lc",
      headerName: "Assigned To",
    },
    ...(tableConfig.projects.dateUpdated ? [dateUpdatedColumn] : []),
  ];

  if (loading) {
    return <Typography>Loading jobs...</Typography>;
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>Recent Jobs</Typography>
      <Box sx={{
        // Hide ALL toolbar buttons (Columns, Filters, Density, Export)
        '& .MuiDataGrid-toolbarContainer': {
          display: 'none !important'
        },
        // Hide only the pagination navigation controls (arrows, page numbers)
        '& .MuiTablePagination-actions': {
          display: 'none !important'
        },
        '& .MuiTablePagination-displayedRows': {
          display: 'none !important'
        },
        '& .MuiTablePagination-selectLabel': {
          display: 'block !important'
        },
        '& .MuiTablePagination-select': {
          display: 'block !important'
        }
      }}>
        <AdvancedTable
          data={jobs}
          collectionName="projects"
          columnsRaw={columns}
          rowCount={jobs.length}
          isInactive={false}
          tableId="dashboard-jobs"
          pageSize={10}
          hideActions={true}
          disableSorting={true}
          disableColumnMenu={true}

        />
      </Box>
    </Box>
  );
};